// =============================================
// TAG MANAGER COMPONENT - Sistema de Tags Coloridas
// Implementación solicitada por Fran - Reunión 15/08/2025
// =============================================

import React, { useState, useEffect, useRef } from 'react';
import { 
  TagIcon, 
  PlusIcon, 
  XMarkIcon,
  ChevronDownIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

// Colores predefinidos para tags
const TAG_COLORS = {
  'URGENTE': { bg: 'bg-red-600', text: 'text-white', border: 'border-red-600' },
  'QUENTE': { bg: 'bg-orange-500', text: 'text-white', border: 'border-orange-500' },
  'INTERESSE': { bg: 'bg-blue-500', text: 'text-white', border: 'border-blue-500' },
  'FOLLOW-UP': { bg: 'bg-yellow-500', text: 'text-black', border: 'border-yellow-500' },
  'QUALIFICADO': { bg: 'bg-green-600', text: 'text-white', border: 'border-green-600' },
  'PERDIDO': { bg: 'bg-gray-500', text: 'text-white', border: 'border-gray-500' },
  'EM-ANDAMENTO': { bg: 'bg-purple-500', text: 'text-white', border: 'border-purple-500' },
  'ENVIADO-PARA-RUI': { bg: 'bg-indigo-600', text: 'text-white', border: 'border-indigo-600' },
  'REAGENDADO': { bg: 'bg-amber-500', text: 'text-black', border: 'border-amber-500' },
  'VIP': { bg: 'bg-pink-600', text: 'text-white', border: 'border-pink-600' }
};

// Componente individual de Tag
const Tag = ({ name, onRemove, removable = true, size = 'sm', className = '' }) => {
  const colors = TAG_COLORS[name?.toUpperCase()] || {
    bg: 'bg-gray-100',
    text: 'text-gray-800',
    border: 'border-gray-300'
  };
  
  const sizeClasses = {
    xs: 'px-1.5 py-0.5 text-xs',
    sm: 'px-2 py-1 text-sm',
    md: 'px-3 py-1.5 text-base',
    lg: 'px-4 py-2 text-lg'
  };
  
  return (
    <span 
      className={`
        inline-flex items-center gap-1 rounded-full font-medium
        ${colors.bg} ${colors.text} ${sizeClasses[size]} 
        border ${colors.border}
        ${className}
      `}
    >
      <TagIcon className="h-3 w-3" />
      <span className="truncate max-w-[120px]" title={name}>
        {name}
      </span>
      {removable && onRemove && (
        <button
          onClick={() => onRemove(name)}
          className={`ml-1 hover:bg-black hover:bg-opacity-20 rounded-full p-0.5 ${colors.text}`}
          title={`Remover tag ${name}`}
        >
          <XMarkIcon className="h-3 w-3" />
        </button>
      )}
    </span>
  );
};

// Componente principal TagManager
const TagManager = ({ 
  contactId,
  currentTags = [], 
  onTagsChange,
  availableTags = [],
  placeholder = "Adicionar tags...",
  maxTags = 10,
  allowCustomTags = true,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredTags, setFilteredTags] = useState([]);
  const [newTagName, setNewTagName] = useState('');
  const [showCreateTag, setShowCreateTag] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Cerrar dropdown cuando se hace click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setShowCreateTag(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filtrar tags disponibles
  useEffect(() => {
    if (!searchTerm) {
      setFilteredTags(availableTags.filter(tag => !currentTags.includes(tag.name)));
    } else {
      const filtered = availableTags.filter(tag => 
        !currentTags.includes(tag.name) &&
        tag.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredTags(filtered);
      
      // Mostrar opción de crear tag si no existe y se permite
      if (allowCustomTags && searchTerm && !availableTags.find(tag => 
        tag.name.toLowerCase() === searchTerm.toLowerCase()
      )) {
        setShowCreateTag(true);
      } else {
        setShowCreateTag(false);
      }
    }
  }, [searchTerm, availableTags, currentTags, allowCustomTags]);

  const handleAddTag = (tagName) => {
    if (currentTags.length >= maxTags) return;
    if (currentTags.includes(tagName)) return;
    
    const updatedTags = [...currentTags, tagName];
    onTagsChange && onTagsChange(updatedTags);
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleRemoveTag = (tagName) => {
    const updatedTags = currentTags.filter(tag => tag !== tagName);
    onTagsChange && onTagsChange(updatedTags);
  };

  const handleCreateCustomTag = () => {
    if (!newTagName.trim()) return;
    
    const formattedName = newTagName.trim().toUpperCase().replace(/\s+/g, '-');
    handleAddTag(formattedName);
    setNewTagName('');
    setShowCreateTag(false);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (showCreateTag && newTagName.trim()) {
        handleCreateCustomTag();
      } else if (filteredTags.length === 1) {
        handleAddTag(filteredTags[0].name);
      }
    } else if (event.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
      setShowCreateTag(false);
    }
  };

  return (
    <div className="relative w-full">
      {/* Tags Atuais */}
      {currentTags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {currentTags.map((tagName) => (
            <Tag
              key={tagName}
              name={tagName}
              onRemove={disabled ? null : handleRemoveTag}
              removable={!disabled}
            />
          ))}
        </div>
      )}

      {/* Input para adicionar tags */}
      {!disabled && (
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full min-h-[36px] px-3 py-2 text-left bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={disabled || currentTags.length >= maxTags}
          >
            <div className="flex items-center justify-between">
              <span className={`${currentTags.length === 0 ? 'text-gray-500' : 'text-gray-900'}`}>
                {currentTags.length === 0 
                  ? placeholder 
                  : `${currentTags.length} tag${currentTags.length !== 1 ? 's' : ''} selecionada${currentTags.length !== 1 ? 's' : ''}`
                }
              </span>
              <div className="flex items-center gap-1">
                {currentTags.length < maxTags && (
                  <PlusIcon className="h-4 w-4 text-gray-400" />
                )}
                <ChevronDownIcon className={`h-4 w-4 text-gray-400 transform transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </div>
            </div>
          </button>

          {/* Dropdown */}
          {isOpen && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
              {/* Search Input */}
              <div className="p-2 border-b">
                <input
                  ref={inputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Buscar tags..."
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  autoFocus
                />
              </div>

              {/* Tags Disponibles */}
              <div className="max-h-48 overflow-y-auto">
                {filteredTags.length > 0 ? (
                  filteredTags.map((tag) => (
                    <button
                      key={tag.name}
                      onClick={() => handleAddTag(tag.name)}
                      className="w-full px-3 py-2 text-left hover:bg-blue-50 flex items-center justify-between group"
                    >
                      <div className="flex items-center gap-2">
                        <Tag 
                          name={tag.name} 
                          removable={false}
                          size="xs"
                        />
                        {tag.description && (
                          <span className="text-xs text-gray-500 truncate">
                            {tag.description}
                          </span>
                        )}
                      </div>
                      <PlusIcon className="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100" />
                    </button>
                  ))
                ) : searchTerm && !showCreateTag ? (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    Nenhuma tag encontrada para "{searchTerm}"
                  </div>
                ) : null}

                {/* Opção para criar tag customizada */}
                {showCreateTag && allowCustomTags && (
                  <div className="px-3 py-2 border-t">
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        type="text"
                        value={newTagName}
                        onChange={(e) => setNewTagName(e.target.value)}
                        onKeyDown={handleKeyPress}
                        placeholder={`Criar tag "${searchTerm}"`}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                      <button
                        onClick={handleCreateCustomTag}
                        className="px-2 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                        title="Criar nova tag"
                      >
                        <CheckIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">
                      A nova tag será criada e aplicada ao contato
                    </p>
                  </div>
                )}

                {/* Estado vazio */}
                {filteredTags.length === 0 && !showCreateTag && !searchTerm && (
                  <div className="px-3 py-8 text-center text-gray-500">
                    <TagIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">Nenhuma tag disponível</p>
                  </div>
                )}
              </div>

              {/* Informações no footer */}
              <div className="px-3 py-2 bg-gray-50 border-t text-xs text-gray-500">
                <div className="flex justify-between items-center">
                  <span>
                    {currentTags.length} / {maxTags} tags
                  </span>
                  {allowCustomTags && (
                    <span>Digite para criar nova tag</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Componente simples para mostrar tags (solo lectura)
export const TagsDisplay = ({ 
  tags = [], 
  size = 'sm', 
  maxVisible = 5,
  showCount = true 
}) => {
  const visibleTags = tags.slice(0, maxVisible);
  const hiddenCount = tags.length - maxVisible;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {visibleTags.map((tagName) => (
        <Tag
          key={tagName}
          name={tagName}
          removable={false}
          size={size}
        />
      ))}
      {hiddenCount > 0 && showCount && (
        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
          +{hiddenCount}
        </span>
      )}
    </div>
  );
};

// Componente para filtros por tags
export const TagsFilter = ({ 
  selectedTags = [], 
  onTagsChange,
  availableTags = [],
  placeholder = "Filtrar por tags...",
  multiple = true
}) => {
  return (
    <TagManager
      currentTags={selectedTags}
      onTagsChange={onTagsChange}
      availableTags={availableTags}
      placeholder={placeholder}
      maxTags={multiple ? 5 : 1}
      allowCustomTags={false}
    />
  );
};

export default TagManager;